import { localTimeFormatsBasedOnDomain } from "../constants/constants";
import { format as dateFormat, format, parseISO } from "date-fns";
import {
  formatColumns,
  skipConversion,
  percentNumberGlobalRegex,
} from "./xlsxFormattingHelper";
import { withDownloadToast } from "./withDownloadToast";

const dateFormatRegex =
  /\b([12]\d{3})[-/](\d{2})[-/](\d{2})\b|\b(\d{1,2})[-/](\d{1,2})[-/]([12]\d{3}|\d{2})\b/g;
const dateFormatSurroundedByOptionalWhitespaceRegex = new RegExp(
  `^\\s*(${dateFormatRegex.source})\\s*$`
);

/**
 * @param convertPercentsBack {boolean}
 * @param exportFormattedValues {boolean}
 * @param numberFormatting {boolean}
 * @param td {HTMLTableCellElement}
 * @returns {string} innerHTML
 */
function performReplacements(
  convertPercentsBack,
  exportFormattedValues,
  numberFormatting,
  td
) {
  const replacedTdInnerHTML = td.innerHTML
    .replaceAll(percentNumberGlobalRegex, (match) => {
      if (convertPercentsBack) {
        const replaced = match.replaceAll(percentNumberGlobalRegex, "");
        return replaced;
      }

      return match;
    })
    // The maximum "safe integer" is 2**53 - 1
    .replaceAll(/(\d+)/gm, (match) => {
      if (Number.MAX_SAFE_INTEGER < +match || (match + "").length > 15) {
        return convertDateToString(match);
      }

      return match;
    })
    // .replaceAll(/\$d+/gm, (match) => (exportFormattedValues ? match : ""))
    .replaceAll(/--/gm, (match) => (exportFormattedValues ? match : ""))
    // .replaceAll(/,/gm, (match) => (exportFormattedValues ? match : ""))
    .replaceAll(/<title (.*?)>(.*?)<\/title>/gm, "");

  const html = document.createElement("div");
  html.innerHTML = replacedTdInnerHTML;

  // Xlsx (at least version 0.16.9) uses new Date(dateString) to convert to an
  // excel date, but that causes timezone discrepancies.
  if (dateFormatSurroundedByOptionalWhitespaceRegex.test(html.textContent)) {
    const dateString = dateFormatRegex.exec(html.textContent)[0];
    // This forces the date to be parsed as a local date,
    // fixing timezone discrepancies.
    const dateIsoParsed = parseISO(dateString);
    const date = dateIsoParsed.getTime() ? dateIsoParsed : new Date(dateString);
    if (date) {
      html.innerHTML = html.innerHTML.replaceAll(
        dateFormatRegex,
        format(date, "yyyy-MM-dd'T'HH:mm:ss")
      );
    }
  }

  // 3-8 => somehow sheet js converting this to date
  html.querySelectorAll("span[data-value]").forEach((span) => {
    if (/^\d+-\d+$/gm.test(span.textContent.trim())) {
      span.textContent = span.textContent + skipConversion;
    }
  });

  // 91302, 91360, 91367, 91303, 91320, 43...
  // for such example we need to put full data value not partial
  html.querySelectorAll("span[data-value]").forEach((span) => {
    if (span.textContent.trim().includes("...")) {
      span.textContent = span.getAttribute("data-value");
    }
  });

  // For ccg replace 999+% to real huge percentage that come from BE
  if (numberFormatting) {
    html.querySelectorAll("span[data-value]").forEach((span) => {
      if (span.textContent.trim() === "999+%") {
        span.textContent = span.getAttribute("data-value");
      }
    });
  }

  return html.innerHTML;
}

/**
 * Strips special characters off of cells to make sure
 *
 * @param table {HTMLTableElement}
 * @param convertPercentsBack {boolean}
 * @param exportFormattedValues {boolean}
 * @param numberFormatting {boolean}
 * @returns {*}
 */
function removeUnnesessarySigns(
  table,
  convertPercentsBack,
  exportFormattedValues,
  numberFormatting
) {
  if (!table) {
    return;
  }

  const cloned = table.cloneNode(true);

  const doPerformReplacements = performReplacements.bind(
    null,
    convertPercentsBack,
    exportFormattedValues,
    numberFormatting
  );

  for (const tableSection of cloned.children) {
    for (const tr of tableSection.children) {
      /** @type {HTMLCollectionOf<HTMLTableCellElement>} */
      const tds = tr.children;
      for (const td of tds) {
        if (td.dataset.v) {
          // SheetJS actually looks for these as attributes, not data-* values.
          td.setAttribute("v", td.dataset.v);
          td.setAttribute("t", td.dataset.t);
          // We're forcing a value here, so do not replace anything.
          continue;
        }
        td.innerHTML = doPerformReplacements(td);
      }
    }
  }

  return cloned;
}

export function clearFileName(name) {
  const cleared = name
    .replaceAll("/", " ")
    .replaceAll("\\", " ")
    .replaceAll("?", " ")
    .replaceAll("*", " ")
    .replaceAll(":", " ")
    .replaceAll("[", " ")
    .replaceAll("]", " ")
    .replaceAll("'", " ");

  return cleared.length > 30 ? cleared.substring(0, 29) : cleared;
}

export const exportXlsx = async (params) => {
  const { filename, tableId } = params;

  const table = document.getElementById(tableId).querySelector("table");
  const ws = await assembleWorksheet(table, params);

  const name = filename || "Insight Out Data";

  await downloadXlsx(ws, name);
};

async function assembleWorksheet(table, params) {
  const {
    convertPercentsBack,
    page = {},
    dates,
    filters,
    domain,
    exportFormattedValues = false,
    exportOriginalValues = false,
    subTitles,
    data,
    meta,
    formatOverrides,
    pinnedHeader,
    rowGroupKeys,
    filename,
    usage,
  } = params;

  const numberFormatting = !exportFormattedValues && !exportOriginalValues;
  const formattedTable = removeUnnesessarySigns(
    table,
    convertPercentsBack,
    exportFormattedValues,
    numberFormatting || exportOriginalValues
  );

  const headerRowCount = formattedTable.querySelectorAll("thead > tr").length;

  if (!formattedTable) {
    return;
  }

  const { utils } = await import("xlsx");

  if (!usage) {
    appendPageName(formattedTable, page);

    appendTableName(formattedTable, filename);

    if (dates && !dates.hideOnPages?.includes(page.uuid)) {
      appendDates(formattedTable, dates);
    }

    appendFilters(formattedTable, filters, page);

    appendCurrentDateTime(formattedTable, domain);
  }

  /* Attila: a better approach to raw/formatting logic would be to ensure in
   * TableVisualization that the cells have the right data-* attributes:
   * https://docs.sheetjs.com/docs/api/utilities/html/#value-override */
  const ws = utils.table_to_sheet(formattedTable, {
    raw: exportFormattedValues,
  });

  // Get table body element
  const tbody = table.querySelector("tbody");

  // Get all table rows inside tbody
  const rows = tbody.querySelectorAll("tr");

  if (rows && rows[0]) {
    const widths = [];

    // Use the first row cells to determine column widths
    const columns = Array.from(rows[0].querySelectorAll("td"));

    columns.forEach((td) => {
      // Measure the rendered width of each column cell
      const { width } = td.getBoundingClientRect();

      // Store width in pixels for worksheet column configuration
      widths.push({ wpx: width });
    });

    // Apply calculated column widths to the worksheet
    ws["!cols"] = widths;
  }

  if (numberFormatting) {
    formatColumns(
      ws,
      utils,
      subTitles,
      data,
      meta,
      formatOverrides,
      pinnedHeader,
      headerRowCount,
      rowGroupKeys
    );
  }

  return ws;
}

async function downloadXlsx(ws, name) {
  const { utils, writeFile } = await import("xlsx");

  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, clearFileName(name));

  const filename = clearFileName(name) + ".xlsx";
  await withDownloadToast(filename, () =>
    writeFile(wb, filename, {
      bookType: "xlsx",
      bookSST: true,
      cellHTML: true,
      type: "binary",
      editable: true,
    })
  );
}

function insertEmptyRow(table, emptyRows) {
  Array(emptyRows)
    .fill("")
    .forEach(() => {
      const tr = document.createElement("tr");
      const td = tr.appendChild(document.createElement("td"));
      td.innerHTML = "";
      table.appendChild(tr);
    });
}

function appendTableName(table, name) {
  if (!name) {
    return;
  }

  insertEmptyRow(table, 1);
  const tr = document.createElement("tr");

  ["Table:", name].forEach((item) => {
    const td = tr.appendChild(document.createElement("td"));
    td.innerHTML = item;
  });

  table.appendChild(tr);
}

function appendPageName(table, page) {
  if (!page) {
    return;
  }

  insertEmptyRow(table, 2);
  const tr = document.createElement("tr");

  ["Page:", page.displayName].forEach((item) => {
    const td = tr.appendChild(document.createElement("td"));
    td.innerHTML = item;
  });

  table.appendChild(tr);
}

function appendDates(table, dates) {
  if (!dates?.start) return;

  insertEmptyRow(table, 1);

  const tr = document.createElement("tr");
  const td = tr.appendChild(document.createElement("td"));
  td.innerHTML = "Dates";

  table.appendChild(tr);

  if (!dates.start.value && !dates.end.value) {
    const td = tr.appendChild(document.createElement("td"));
    td.innerHTML = "All Time";
    table.appendChild(tr);
    return;
  }

  const start = new Date(dates.start.value);
  const end = new Date(dates.end.value);

  const timezoneOffset = start.getTimezoneOffset() / 60 + 1;

  start.setHours(start.getHours() + timezoneOffset);
  end.setHours(end.getHours() + timezoneOffset);

  [
    { value: start, label: "Start" },
    { value: end, label: "End" },
  ].forEach((item) => {
    const tr = document.createElement("tr");
    const label = tr.appendChild(document.createElement("td"));
    const value = tr.appendChild(document.createElement("td"));
    label.innerHTML = item.label;
    value.innerHTML = format(item.value, "MM/dd/yyyy") + skipConversion;

    table.appendChild(tr);
  });
}

function appendFilters(table, filters = [], page) {
  const activeFilters = filters
    .filter((f) => {
      if (f.show) {
        return f.show.includes(page.uuid);
      }
      return !f.hide?.includes(page.uuid);
    })
    .reduce((acc, curr) => {
      const checked = curr.values.filter((v) => v.checked);
      if (checked.length) {
        acc.push({ ...curr, values: checked });
      }

      return acc;
    }, []);

  if (!activeFilters.length) {
    return;
  }

  insertEmptyRow(table, 1);

  const tr = document.createElement("tr");
  const td = document.createElement("td");

  td.innerHTML = "Filters";
  tr.appendChild(td);
  table.appendChild(tr);

  activeFilters.forEach((filter) => {
    const tr = document.createElement("tr");
    const td = document.createElement("td");

    td.innerHTML = filter.displayName;
    tr.appendChild(td);

    filter.values.forEach((v) => {
      const td = document.createElement("td");
      td.innerHTML = v.label + skipConversion;
      tr.appendChild(td);
    });

    table.appendChild(tr);
  });
}

function appendCurrentDateTime(table, domain) {
  const { format } = localTimeFormatsBasedOnDomain[domain] ?? {};

  if (!format) {
    return;
  }

  const date = dateFormat(Date.now(), format);

  insertEmptyRow(table, 1);

  const tr = document.createElement("tr");
  const name = tr.appendChild(document.createElement("td"));
  name.innerHTML = `Local Date: `;

  const value = tr.appendChild(document.createElement("td"));
  value.innerHTML = date + skipConversion;

  table.appendChild(tr);
}

function convertDateToString(date) {
  return date + invisibleSymbol;
}

// Use ZERO WIDTH NO-BREAK SPACE (U+FEFF) to coerce text in Excel without rendering artifacts on macOS
// Previously used U+200D (ZERO WIDTH JOINER) which can surface as odd spacing in Excel for Mac
const invisibleSymbol = "&#65279;";

export const exportedForTesting = {
  assembleWorksheet,
  removeUnnesessarySigns,
};
